import { useLingui } from "@lingui/react";
import { isPrivateAdvertiser } from "@mobility-item-page/utilities";
import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import { Button } from "@warp-ds/react";
import { lazy, Suspense, useState } from "react";
import { useAdViewMeta } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import { Icon } from "../Icon.tsx";
import { ScreenReaderSeparator } from "../ScreenReaderSeparator.tsx";

const EmbeddedVideo = lazy(() => import("./EmbeddedVideo.tsx"));

type Props = {
  video_url: string;
};

const Loading = () => {
  return <div className="animate-spinner [--spinner-size:32px]"></div>;
};

export const VideoButton = ({ video_url }: Props) => {
  const { i18n } = useLingui();
  const { proGatewayFeatures } = useAppConfig();
  const { trackClick } = usePulse();
  const { ownerUrn } = useAdViewMeta();
  const isPrivateAd = isPrivateAdvertiser(ownerUrn);
  const [videoVisible, setVideoVisible] = useState(false);

  const shouldShowVideo = () => {
    if (isPrivateAd) return true;
    return proGatewayFeatures.DisplayVideos;
  };
  if (!shouldShowVideo()) return null;

  const toggle = () => {
    if (!videoVisible) {
      trackClick({
        name: "Item video",
        objectId: "MO_ev68",
        elementType: ElementType.Button,
        intent: Intent.Open,
      });
    }
    setVideoVisible((prevValue) => !prevValue);
  };

  const videoButtonText = i18n._({
    id: "itemPageLayout.video.openButton.text",
    message: "View video",
    comment: "Button for showing attached video for an ad",
  });

  return (
    <>
      <Button className="flex items-center px-12! shrink-0" quiet utility onClick={toggle}>
        <Icon name="Play" size="medium" />
        <ScreenReaderSeparator />
        <span className="ml-4">{videoButtonText}</span>
      </Button>
      {videoVisible && (
        <Suspense fallback={<Loading />}>
          <EmbeddedVideo video_url={video_url} toggle={toggle} />
        </Suspense>
      )}
    </>
  );
};
